'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { useAuth } from '@/hooks/auth/use-auth'
import { isVisibleSkipMarker } from '@/lib/conversation/conversation-events'
import type {
  CapturedCustomerInfo,
  ChannelType,
  ConversationStatus,
  MessageDirection,
  MessageType,
} from '@/lib/agents/messaging/types'
import type { ThreadQuality } from '@/lib/conversation/thread-header'

// Re-export for use in components
export type { MessageType, MessageDirection }

export interface AIReplyMeta {
  sentiment_score: number | null
  escalated: boolean
  escalated_reason: string | null
  skipped: boolean
  skip_reason: string | null
  classification_label: string | null
  can_close: boolean | null
  dry_run: boolean
  ai_request_log_id: string | null
}

export interface ConversationMessage {
  id: string
  direction: MessageDirection
  message_type: MessageType
  content_text: string | null
  content_media_url: string | null
  /** Attachment-forcing URL (private storage only); null for platform-CDN media. */
  content_media_download_url?: string | null
  content_payload: Record<string, unknown> | null
  is_ai_generated: boolean
  created_at: string
  sent_at: string | null
  delivered_at: string | null
  read_at: string | null
  ai_reply: AIReplyMeta | null
}

/** Message types whose media is actually rendered by the bubble's MediaContent. */
const MEDIA_RENDER_TYPES: MessageType[] = ['image', 'story_reply', 'video', 'audio', 'file']

/**
 * Whether a message has anything to show. Story mentions, blank inbound noise and
 * media-less operator echoes carry neither text nor displayable media — we hide
 * them rather than draw an empty bubble. `session_start` and `reaction` always
 * render (separator / emoji).
 */
export function isRenderableMessage(msg: ConversationMessage): boolean {
  if (msg.message_type === 'session_start' || msg.message_type === 'reaction') return true
  // Delivery-failure notice: no text/media of its own, reason lives in content_payload.
  if (msg.message_type === 'send_failed') return true
  // Same shape: the escalation alert marker carries only an optional reason.
  if (msg.message_type === 'handover_notified') return true
  // The thread route filters on the same rule.
  if (msg.message_type === 'reply_skipped') return isVisibleSkipMarker(msg.content_payload)
  if (msg.content_text && msg.content_text.trim().length > 0) return true
  if (msg.content_media_url && MEDIA_RENDER_TYPES.includes(msg.message_type)) return true
  // Degraded private-storage media (over the 20 MB download cap, failed
  // download, failed URL signing) has no URL but carries file metadata — it
  // renders by filename. Hiding the bubble would look like a lost message:
  // the AI replies to these (isReplyableInbound accepts the same metadata),
  // so the inbound must stay visible above the reply.
  if (
    MEDIA_RENDER_TYPES.includes(msg.message_type) &&
    typeof msg.content_payload?.file_name === 'string'
  ) {
    return true
  }
  return false
}

export interface ConversationDetail {
  id: string
  channel_type: ChannelType
  contact_name: string | null
  /** Platform handle (`@username`) — Instagram and Telegram; the exclusion-list key. */
  contact_handle: string | null
  contact_avatar_url: string | null
  status: ConversationStatus
  ai_paused: boolean
  /** Conversation-level opt-out from the agent's «pause AI on human intervention» setting. */
  auto_pause_exempt: boolean
  /** Pause origin (`metadata.ai_pause.by`); null for active or legacy rows. */
  ai_pause_by: string | null
  /** `metadata.ai_pause.reason` — for an escalation, the model's own words. */
  ai_pause_reason: string | null
  external_user_id: string
  created_at: string
  channel_account_name: string | null
  customer_info: CapturedCustomerInfo | null
  crm_opportunity_url: string | null
  /** For `web_demo` conversations: the Shopify storefront domain the visitor
      picked at `/set-domain`. `null` for any other channel type. */
  shopify_demo_domain: string | null
  /** When the next scheduled follow-up fires. `null` = nothing scheduled. */
  pending_follow_up_at: string | null
  /** The verdict on the last judged session. `null` until one has been reviewed. */
  quality: ThreadQuality | null
}

/** Authoritative auto-pause state returned by the conversation PATCH. */
export interface ConversationPauseFlags {
  ai_paused: boolean
  auto_pause_exempt: boolean
}

const MESSAGES_POLL_INTERVAL = 5_000

export function useMessages(agentId: string | null, onNewInbound?: (conversationId: string) => void) {
  const [conversation, setConversation] = useState<ConversationDetail | null>(null)
  const [messages, setMessages] = useState<ConversationMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const cursorRef = useRef<string | null>(null)
  const conversationIdRef = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const latestMessageRef = useRef<{ created_at: string; id: string } | null>(null)
  const pollingRef = useRef(false)
  const { fetchWithAuth } = useAuth()


  const fetchMessages = useCallback(async (conversationId: string, reset = true) => {
    if (!agentId) return

    // Abort any in-flight request
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    conversationIdRef.current = conversationId
    setIsLoading(true)

    try {
      const params = new URLSearchParams()
      if (!reset && cursorRef.current) {
        params.set('cursor', cursorRef.current)
      }

      const qs = params.toString()
      const res = await fetchWithAuth(
        `/api/agents/messaging/${agentId}/conversations/${conversationId}${qs ? `?${qs}` : ''}`,
        { signal: controller.signal },
      )
      if (!res.ok) throw new Error('Failed to fetch messages')

      const data = await res.json()

      // Stale response guard: ignore if user already switched conversations
      if (conversationIdRef.current !== conversationId) return

      setConversation(data.conversation)

      if (reset) {
        setMessages(data.messages)
      } else {
        // Prepend older messages, deduping against existing state in case the cursor returned
        // an overlapping page (rare but cheap to guard).
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m: ConversationMessage) => m.id))
          const fresh = (data.messages as ConversationMessage[]).filter(
            (m) => !existingIds.has(m.id)
          )
          return [...fresh, ...prev]
        })
      }

      // Track newest message for forward polling (null = no messages → polling skipped)
      if (reset) {
        if (data.messages.length > 0) {
          const newest = data.messages[data.messages.length - 1]
          latestMessageRef.current = { created_at: newest.created_at, id: newest.id }
        } else {
          latestMessageRef.current = null
        }
      }

      cursorRef.current = data.next_cursor
      setHasMore(data.has_more)
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      console.error('[useMessages] Error:', err)
    } finally {
      if (controller === abortRef.current) {
        setIsLoading(false)
      }
    }
  }, [agentId, fetchWithAuth])

  const openConversation = useCallback((conversationId: string) => {
    // Clear previous state — triggers full skeleton (header + messages)
    setMessages([])
    setConversation(null)
    cursorRef.current = null
    latestMessageRef.current = null
    fetchMessages(conversationId, true)
    // Mark messages as read (fire-and-forget)
    fetchWithAuth(
      `/api/agents/messaging/${agentId}/conversations/${conversationId}/messages/read`,
      { method: 'POST' }
    ).catch(() => {})
  }, [fetchMessages, fetchWithAuth, agentId])

  const loadOlder = useCallback(() => {
    if (hasMore && !isLoading && conversationIdRef.current) {
      fetchMessages(conversationIdRef.current, false)
    }
  }, [hasMore, isLoading, fetchMessages])

  const refresh = useCallback(async () => {
    if (conversationIdRef.current) {
      cursorRef.current = null
      await fetchMessages(conversationIdRef.current, true)
    }
  }, [fetchMessages])

  // Silent poll for new messages (forward cursor)
  const pollNewMessages = useCallback(async () => {
    const convId = conversationIdRef.current
    if (!agentId || !convId || !latestMessageRef.current) return
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
    if (pollingRef.current) return // skip if poll already in flight

    pollingRef.current = true
    try {
      const after = `${latestMessageRef.current.created_at}|${latestMessageRef.current.id}`
      const res = await fetchWithAuth(
        `/api/agents/messaging/${agentId}/conversations/${convId}?after=${encodeURIComponent(after)}`,
      )
      if (!res.ok) return

      const data = await res.json()
      if (conversationIdRef.current !== convId) return // stale guard

      // Update conversation details (status may have changed)
      setConversation(data.conversation)

      if (data.messages.length > 0) {
        // Deduplicate and append
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id))
          const fresh = (data.messages as ConversationMessage[]).filter((m) => !existingIds.has(m.id))
          return fresh.length > 0 ? [...prev, ...fresh] : prev
        })
        const newest = data.messages[data.messages.length - 1]
        latestMessageRef.current = { created_at: newest.created_at, id: newest.id }

        // Mark new inbound messages as read (conversation is open)
        const hasInbound = data.messages.some((m: ConversationMessage) => m.direction === 'inbound')
        if (hasInbound) {
          fetchWithAuth(
            `/api/agents/messaging/${agentId}/conversations/${convId}/messages/read`,
            { method: 'POST' }
          ).catch(() => {})
          onNewInbound?.(convId)
        }
      }
    } catch {
      // Silent fail — polling errors are not actionable
    } finally {
      pollingRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onNewInbound is an external callback; restarting polling on its identity churn would be noisy and pointless
  }, [agentId, fetchWithAuth])

  // Polling interval — restarts when conversation changes
  useEffect(() => {
    if (!conversation) return

    const intervalId = setInterval(pollNewMessages, MESSAGES_POLL_INTERVAL)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') pollNewMessages()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- restart polling only when conversation ID changes, not on status updates
  }, [conversation?.id, pollNewMessages])

  // Send a manual operator message. Returns { ai_paused } from server.
  const sendMessage = useCallback(async (text: string): Promise<{ ai_paused: boolean }> => {
    const convId = conversationIdRef.current
    if (!agentId || !convId) return { ai_paused: false }

    const res = await fetchWithAuth(
      `/api/agents/messaging/${agentId}/conversations/${convId}/messages`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      },
    )

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to send message')
    }

    // Stale guard: user may have switched conversations while request was in flight
    if (conversationIdRef.current !== convId) return { ai_paused: false }

    const data = await res.json()
    const sent = data.message as ConversationMessage

    // Append idempotently: a 5s polling tick can fire between server-side store and our response,
    // fetch the new row first, and add it to state. Without the .some() guard the response then
    // appends a SECOND copy — React fires «two children with the same key» and the bubble shows
    // twice. Polling already dedups its own additions; we just mirror that contract here.
    setMessages((prev) => {
      if (prev.some((m) => m.id === sent.id)) return prev
      return [...prev, { ...sent, ai_reply: null, content_media_url: null, content_payload: null, delivered_at: null, read_at: null }]
    })
    latestMessageRef.current = { created_at: sent.created_at, id: sent.id }

    // Update AI pause state from server response (accurate, not optimistic)
    const paused = data.ai_paused === true
    if (paused) {
      setConversation((prev) => prev ? { ...prev, ai_paused: true } : prev)
    }

    return { ai_paused: paused }
  }, [agentId, fetchWithAuth])

  // One endpoint drives both auto-pause flags, and enabling the exemption also resumes the AI —
  // so local state comes from the server response, never from the requested value.
  // Returns the authoritative flags, or null when the conversation switched mid-request.
  const patchConversation = useCallback(async (
    patch: { ai_paused?: boolean; auto_pause_exempt?: boolean },
  ): Promise<ConversationPauseFlags | null> => {
    const convId = conversationIdRef.current
    if (!agentId || !convId) return null

    const res = await fetchWithAuth(
      `/api/agents/messaging/${agentId}/conversations/${convId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    )

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to update conversation')
    }

    const { conversation: updated } = (await res.json()) as { conversation: ConversationPauseFlags }

    // Stale guard: ignore if conversation switched during the request
    if (conversationIdRef.current !== convId) return null

    setConversation((prev) => prev ? {
      ...prev,
      ai_paused: updated.ai_paused,
      auto_pause_exempt: updated.auto_pause_exempt,
      ai_pause_by: updated.ai_paused ? prev.ai_pause_by : null,
      ai_pause_reason: updated.ai_paused ? prev.ai_pause_reason : null,
    } : prev)

    return updated
  }, [agentId, fetchWithAuth])

  // Toggle AI auto-reply for this conversation
  const toggleAiPaused = useCallback(
    (paused: boolean) => patchConversation({ ai_paused: paused }),
    [patchConversation],
  )

  // Exempt this conversation from the agent's auto-pause-on-human setting (and resume the AI)
  const toggleAutoPauseExempt = useCallback(
    (exempt: boolean) => patchConversation({ auto_pause_exempt: exempt }),
    [patchConversation],
  )

  // Start a new session: reset CRM, unpause AI, insert session marker
  const startNewSession = useCallback(async () => {
    const convId = conversationIdRef.current
    if (!agentId || !convId) return

    const res = await fetchWithAuth(
      `/api/agents/messaging/${agentId}/conversations/${convId}/new-session`,
      { method: 'POST' },
    )

    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to start new session')
    }

    if (conversationIdRef.current !== convId) return

    const data = await res.json()

    // Unpause AI in local state
    setConversation((prev) => prev ? { ...prev, ai_paused: data.ai_paused ?? false } : prev)

    // Refresh to load the session_start marker
    cursorRef.current = null
    await fetchMessages(convId, true)
  }, [agentId, fetchWithAuth, fetchMessages])

  return { conversation, messages, isLoading, hasMore, openConversation, loadOlder, refresh, sendMessage, toggleAiPaused, toggleAutoPauseExempt, startNewSession }
}
